import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToTeamChats,
  subscribeToTeamChatMessages,
  sendTeamChatMessage,
  markTeamChatMessagesAsRead,
  updateEmployeeStatus
} from '../services/firestoreService';
import './TeamchatScreen.css';

const TeamchatScreen = ({ onClose }) => {
  const { employeeData, currentUser } = useAuth();
  const [selectedChat, setSelectedChat] = useState(null);
  const [teamChats, setTeamChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const unsubscribeChatsRef = useRef(null);

  // Update employee status when team chat opens/closes
  useEffect(() => {
    const employeeId = employeeData?._id;
    
    if (!employeeId) {
      return;
    }

    // Set status to online when team chat screen opens
    updateEmployeeStatus(employeeId, 'online').catch(error => {
      console.error('Error updating employee status to online:', error);
    });

    // Cleanup: Set status to offline when component unmounts (team chat closes)
    return () => {
      if (employeeId) {
        updateEmployeeStatus(employeeId, 'offline').catch(error => {
          console.error('Error updating employee status to offline:', error);
        });
      }
    };
  }, [employeeData?._id]);

  // Subscribe to real-time team chats updates
  useEffect(() => {
    const currentUserId = employeeData?._id || currentUser?.uid;
    
    if (!currentUserId) {
      return;
    }

    if (unsubscribeChatsRef.current) {
      unsubscribeChatsRef.current();
    }

    unsubscribeChatsRef.current = subscribeToTeamChats(currentUserId, (updatedChats) => {
      setTeamChats(updatedChats);
      setLoadingChats(false);
    });

    return () => {
      if (unsubscribeChatsRef.current) {
        unsubscribeChatsRef.current();
      }
    };
  }, [employeeData, currentUser]);

  // Subscribe to real-time messages when chat is selected
  useEffect(() => {
    if (selectedChat) {
      setLoadingMessages(true);
      
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      
      const currentUserId = employeeData?._id || currentUser?.uid;
      markTeamChatMessagesAsRead(selectedChat.id, currentUserId)
        .then(() => {
          setTeamChats(prevChats => 
            prevChats.map(chat => 
              chat.id === selectedChat.id 
                ? { ...chat, unread: 0 }
                : chat
            )
          );
        })
        .catch(err => {
          console.error('Error marking messages as read:', err);
        });
      
      unsubscribeRef.current = subscribeToTeamChatMessages(selectedChat.id, (newMessages) => {
        setMessages(newMessages);
        setLoadingMessages(false);
        
        const currentUserId = employeeData?._id || currentUser?.uid;
        const unreadCount = newMessages.filter(msg => {
          if (msg.from === currentUserId || msg.from === 'admin') {
            return false;
          }
          return !msg.isRead;
        }).length;
        
        setTeamChats(prevChats => 
          prevChats.map(chat => 
            chat.id === selectedChat.id 
              ? { ...chat, unread: unreadCount }
              : chat
          )
        );
        
        setTimeout(() => {
          if (messagesContainerRef.current) {
            const container = messagesContainerRef.current;
            container.scrollTop = container.scrollHeight;
          }
        }, 100);
      });
      
      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
        }
      };
    } else {
      setMessages([]);
    }
  }, [selectedChat, employeeData, currentUser]);

  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedChat) return;

    const messageText = inputMessage.trim();
    setInputMessage('');
    inputRef.current?.focus();

    try {
      const senderName = employeeData?.name || employeeData?.employeeName || 'Employee';
      const senderId = employeeData?._id || currentUser?.uid;
      
      await sendTeamChatMessage(selectedChat.id, messageText, senderId, senderName);
      
      setTeamChats(prevChats => 
        prevChats.map(chat => 
          chat.id === selectedChat.id 
            ? { 
                ...chat, 
                lastMessage: messageText,
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                updatedAt: new Date(),
                unread: 0
              }
            : chat
        ).sort((a, b) => {
          const timeA = a.updatedAt instanceof Date ? a.updatedAt.getTime() : 0;
          const timeB = b.updatedAt instanceof Date ? b.updatedAt.getTime() : 0;
          return timeB - timeA;
        })
      );
      
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
      setInputMessage(messageText);
    }
  };

  const filteredChats = teamChats.filter(chat =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="teamchat-screen-container">
      <div className="teamchat-container">
        {/* Sidebar - Chat List */}
        <div className={`teamchat-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <h2>Team Chats</h2>
          </div>
          
          <div className="search-container">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Search chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="chat-list">
            {loadingChats ? (
              <div className="loading-state">
                <p>Loading chats...</p>
              </div>
            ) : filteredChats.length === 0 ? (
              <div className="empty-state">
                <p>No team chats found.</p>
              </div>
            ) : (
              filteredChats.map(chat => (
              <div
                key={chat.id}
                className={`chat-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                onClick={() => handleChatSelect(chat)}
              >
                <div className="chat-avatar">
                  {chat.avatar}
                  {chat.online && <span className="online-indicator"></span>}
                </div>
                <div className="chat-info">
                  <div className="chat-header-row">
                    <h3 className="chat-name">{chat.name}</h3>
                    <span className="chat-time">{chat.time}</span>
                  </div>
                  <div className="chat-preview-row">
                    <p className="chat-preview">{chat.lastMessage}</p>
                    {chat.unread > 0 && (
                      <span className="unread-badge">{chat.unread}</span>
                    )}
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="teamchat-main">
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <div className="chat-header-main">
                <div className="chat-header-left">
                  <button 
                    className="mobile-menu-btn"
                    onClick={toggleSidebar}
                    aria-label="Toggle sidebar"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="3" y1="12" x2="21" y2="12"></line>
                      <line x1="3" y1="6" x2="21" y2="6"></line>
                      <line x1="3" y1="18" x2="21" y2="18"></line>
                    </svg>
                  </button>
                  <div className="chat-avatar-main">
                    {selectedChat.avatar}
                    {selectedChat.online && <span className="online-indicator-main"></span>}
                  </div>
                  <div className="chat-header-info">
                    <h3>{selectedChat.name}</h3>
                    <span className="chat-status">
                      {selectedChat.online ? 'online' : 'offline'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="messages-container" ref={messagesContainerRef}>
                {loadingMessages ? (
                  <div className="loading-state">
                    <p>Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="chat-empty-state">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((message, index) => {
                    const currentUserId = employeeData?._id || currentUser?.uid;
                    const isMe = message.from === 'admin' || message.from === currentUserId;
                    const showAvatar = index === 0 || 
                      messages[index - 1].from !== message.from ||
                      (new Date(message.timestamp) - new Date(messages[index - 1].timestamp)) > 300000;

                    const messageDate = new Date(message.timestamp);
                    const prevMessageDate = index > 0 ? new Date(messages[index - 1].timestamp) : null;
                    const showDateSeparator = !prevMessageDate || 
                      messageDate.toDateString() !== prevMessageDate.toDateString();

                    const formatDateSeparator = (date) => {
                      const today = new Date();
                      const yesterday = new Date(today);
                      yesterday.setDate(yesterday.getDate() - 1);
                      
                      const messageDateStr = date.toDateString();
                      const todayStr = today.toDateString();
                      const yesterdayStr = yesterday.toDateString();
                      
                      if (messageDateStr === todayStr) {
                        return 'Today';
                      } else if (messageDateStr === yesterdayStr) {
                        return 'Yesterday';
                      } else {
                        return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                      }
                    };

                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <div className="date-separator">
                          <span className="date-separator-text">{formatDateSeparator(messageDate)}</span>
                        </div>
                      )}
                      <div
                        className={`message-wrapper ${isMe ? 'message-me' : 'message-other'}`}
                      >
                        {!isMe && showAvatar && (
                          <div className="message-avatar">
                            {message.fromName ? message.fromName.substring(0, 2).toUpperCase() : selectedChat.avatar}
                          </div>
                        )}
                        {!isMe && !showAvatar && <div className="message-avatar-spacer"></div>}
                        <div className="message-bubble-wrapper">
                          {!isMe && showAvatar && (
                            <span className="message-sender-name">{message.senderName || message.fromName || 'Unknown'}</span>
                          )}
                          <div className={`message-bubble ${isMe ? 'bubble-me' : 'bubble-other'}`}>
                            <p className="message-text">{message.text || message.message}</p>
                            <span className="message-time-bubble">
                              {message.time || (message.timestamp ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')}
                              {isMe && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20,6 9,17 4,12"></polyline>
                                </svg>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="input-container">
                <form onSubmit={handleSendMessage} className="input-form">
                  <input
                    ref={inputRef}
                    type="text"
                    className="message-input"
                    placeholder="Type a message..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                  />
                  {inputMessage.trim() ? (
                    <button type="submit" className="send-button">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    </button>
                  ) : (
                    <button type="button" className="input-action-btn" title="Emoji">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                        <line x1="9" y1="9" x2="9.01" y2="9"></line>
                        <line x1="15" y1="9" x2="15.01" y2="9"></line>
                      </svg>
                    </button>
                  )}
                </form>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <button 
                className="mobile-menu-btn-center"
                onClick={toggleSidebar}
                aria-label="Open chat list"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <h3>Select a chat to start messaging</h3>
              <p>Choose a team chat from the sidebar to begin conversation</p>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>
      )}
    </div>
  );
};

export default TeamchatScreen;



